/**
 * Sentry Error Monitoring for Frontend
 * Enterprise-grade error tracking and performance monitoring
 */

import * as Sentry from '@sentry/react';

/**
 * A real Sentry DSN is `https://<hex publicKey>@<host>/<numeric projectId>`.
 * Reject placeholders (e.g. the `your_sentry_dsn@.../1234567` shipped in
 * .env.example) so a dummy value in REACT_APP_SENTRY_DSN doesn't masquerade
 * as configured monitoring while silently dropping every event.
 */
const isValidSentryDsn = (dsn) => {
  if (!dsn || typeof dsn !== 'string') return false;
  try {
    const url = new URL(dsn.trim());
    const projectId = url.pathname.replace(/^\/+/, '');
    return (
      /^https?:$/.test(url.protocol) &&
      !!url.hostname &&
      /^[a-f0-9]{16,}$/i.test(url.username) && // real keys are long hex
      /^\d+$/.test(projectId)
    );
  } catch {
    return false;
  }
};

// Initialize Sentry
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!isValidSentryDsn(dsn)) {
    if (dsn) {
      // eslint-disable-next-line no-console
      console.warn('[Sentry] REACT_APP_SENTRY_DSN is set but invalid (placeholder?) — frontend error monitoring disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_VERSION || '1.0.0',

    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session Replay — error-only to stay within Sentry's replay quota.
    // 0 = don't record random "everything worked" sessions (those were the
    // volume driver); 1.0 = still capture every session that hits an error,
    // which is the part that's actually useful for debugging.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Filter errors
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Don't send network errors (usually user's connection issue)
      if (error?.message?.includes('Network Error') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('Load failed')) {
        return null;
      }

      // Don't send 401/403 errors (expected auth failures)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return null;
      }

      // Don't send cancelled requests
      if (error?.code === 'ERR_CANCELED') {
        return null;
      }

      // Sanitize sensitive data from request body
      if (event.request?.data) {
        try {
          const data = typeof event.request.data === 'string'
            ? JSON.parse(event.request.data)
            : event.request.data;

          const sanitized = { ...data };
          ['password', 'newPassword', 'currentPassword', 'confirmPassword', 'token', 'apiKey', 'streamKey', 'creditCard'].forEach(key => {
            if (sanitized[key]) sanitized[key] = '[REDACTED]';
          });
          event.request.data = JSON.stringify(sanitized);
        } catch (e) {
          // Ignore parse errors
        }
      }

      return event;
    },

    // Ignore common browser errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.telerik.com/',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'conduitPage',

      // Common browser issues
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Load failed',
      'ChunkLoadError',

      // User actions
      'AbortError',
      'The operation was aborted',
      'User denied geolocation permission',

      // Third party
      'Script error',
      'Blocked a frame with origin',

      // Meta's in-app browsers (Instagram / Facebook) inject their own
      // JavaScript into every page they load via WKUserScript, which runs in
      // the page's context and bypasses our CSP. That injected code talks to
      // the native app through the WKWebView bridge
      // (`window.webkit.messageHandlers`), and throws a TypeError when the
      // bridge isn't present in the frame it landed in. Our SDK's
      // window.onerror hook then attributes a third-party throw to whatever
      // page it happened on — showing up as e.g. a TypeError on
      // /register/:slug with a stack pointing at the HTML document (line 1,
      // i.e. an inline script) rather than at /assets/*.js.
      //
      // Every ad click that opens a landing page inside the Instagram app
      // produces one of these, which buries real alerts. Safe to drop
      // wholesale: `messageHandlers` appears nowhere in our own source (we
      // only use webkitAudioContext / webkitSpeechRecognition), so any event
      // naming it is third-party by definition. Regex, not a literal string,
      // because the wording of the TypeError differs across engines.
      /messageHandlers/,
    ],

    // Deny specific URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
      // Edge extensions
      /^ms-browser-extension:\/\//i,
    ],

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });

};

// Set user context
export const setUser = (user) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
      plan: user.plan,
    });
  } else {
    Sentry.setUser(null);
  }
};

// Capture exception with context
export const captureException = (error, context = {}) => {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.error('Error:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    // Add context
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    // Add tags
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    Sentry.captureException(error);
  });
};

// Capture message
export const captureMessage = (message, level = 'info', context = {}) => {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console[level === 'error' ? 'error' : 'log'](message, context);
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
};

// Add breadcrumb
export const addBreadcrumb = (breadcrumb) => {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    timestamp: Date.now() / 1000,
    ...breadcrumb,
  });
};

// Error boundary wrapper
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Wrap component with error boundary
export const withErrorBoundary = (Component, fallback) => {
  return Sentry.withErrorBoundary(Component, {
    fallback: fallback || <ErrorFallback />,
  });
};

// Default error fallback component
const ErrorFallback = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    textAlign: 'center',
  }}>
    <h2 style={{ marginBottom: '1rem', color: '#e53e3e' }}>Something went wrong</h2>
    <p style={{ marginBottom: '1.5rem', color: '#666' }}>
      We've been notified and are working to fix the issue.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: '#D4B560',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
      }}
    >
      Refresh Page
    </button>
  </div>
);

export default Sentry;
